import type { BodyPose } from './bodyPoseFrame.ts';

/**
 * Забег на headless-ТВ (GEN-18912) — то, что раньше делал телефон: калибровка «встаньте в зону», автопауза при
 * потере игрока, итог один раз за забег. Без DOM и таймеров: время приходит в `tick`.
 *
 * calibration ──заполнили кольцо──► starting ──room:game-start──► playing ⇄ paused
 *      ▲                                                             │ finished
 *      └──────────────────────── «Ещё раз» ◄──── result ◄────────────┘
 */
export type HeadlessPhase = 'idle' | 'calibration' | 'starting' | 'playing' | 'paused' | 'result';

/** Тело в зоне столько — кольцо заполнено, забег стартует. */
export const CALIBRATION_MS = 1500;

/** Тела нет столько — пауза. */
export const BODY_LOST_PAUSE_MS = 2000;

/** Тело вернулось и держится столько — игра продолжается (без мигания паузы на одном пропущенном кадре). */
export const BODY_BACK_RESUME_MS = 500;

/** Экран итога не принимает нажатий столько — «Ещё раз» не жмётся рукой, которая только что летела. */
export const RESULT_GUARD_MS = 1500;

/** Отступ от края кадра: точка у самой рамки — уже почти потеряна. */
const FRAME_MARGIN = 0.03;

export type HeadlessAction = 'start' | 'pause' | 'resume';

/** Плечи и запястья видны и в кадре — этого хватает самолёту. */
export function upperBodyInView(pose: BodyPose | null): boolean {
  if (!pose) return false;
  return [pose.leftShoulder, pose.rightShoulder, pose.leftWrist, pose.rightWrist].every(
    (p) => p !== undefined
      && p.x >= FRAME_MARGIN && p.x <= 1 - FRAME_MARGIN && p.y >= FRAME_MARGIN && p.y <= 1 - FRAME_MARGIN,
  );
}

export class HeadlessSession {
  private phaseValue: HeadlessPhase = 'idle';

  private progress = 0;

  private lastTickMs = 0;

  private lostSinceMs: number | null = null;

  private backSinceMs: number | null = null;

  private reported = false;

  get phase(): HeadlessPhase {
    return this.phaseValue;
  }

  /** Заполнение кольца калибровки, 0…1. */
  get calibration(): number {
    return this.progress;
  }

  /** Новый забег (первый или «Ещё раз»). */
  beginCalibration(nowMs: number): void {
    this.phaseValue = 'calibration';
    this.progress = 0;
    this.lastTickMs = nowMs;
    this.reported = false;
  }

  /** Сервер подтвердил старт (`room:game-start`). */
  started(): void {
    if (this.phaseValue !== 'starting' && this.phaseValue !== 'calibration') return;
    this.phaseValue = 'playing';
    this.lostSinceMs = null;
  }

  tick(nowMs: number, pose: BodyPose | null): HeadlessAction | null {
    const dt = Math.max(0, nowMs - this.lastTickMs);
    this.lastTickMs = nowMs;
    const inView = upperBodyInView(pose);

    switch (this.phaseValue) {
    case 'calibration':
      // Вышел из зоны — кольцо заново: старт только с игроком на месте.
      this.progress = inView ? Math.min(1, this.progress + dt / CALIBRATION_MS) : 0;
      if (this.progress < 1) return null;
      this.phaseValue = 'starting';
      return 'start';
    case 'playing':
      if (inView) {
        this.lostSinceMs = null;
        return null;
      }
      this.lostSinceMs ??= nowMs;
      if (nowMs - this.lostSinceMs < BODY_LOST_PAUSE_MS) return null;
      this.phaseValue = 'paused';
      this.backSinceMs = null;
      return 'pause';
    case 'paused':
      if (!inView) {
        this.backSinceMs = null;
        return null;
      }
      this.backSinceMs ??= nowMs;
      if (nowMs - this.backSinceMs < BODY_BACK_RESUME_MS) return null;
      this.phaseValue = 'playing';
      this.lostSinceMs = null;
      return 'resume';
    default:
      return null;
    }
  }

  /** Страница сказала `finished`. `true` — итог этого забега ещё не отправлен: отправить телефону. */
  finished(): boolean {
    if (this.phaseValue !== 'playing' && this.phaseValue !== 'paused' && this.phaseValue !== 'starting') return false;
    this.phaseValue = 'result';
    if (this.reported) return false;
    this.reported = true;
    return true;
  }

  /** Старт не прошёл (сервер отказал) — снова калибровка. */
  startFailed(nowMs: number): void {
    if (this.phaseValue === 'starting') this.beginCalibration(nowMs);
  }

  stop(): void {
    this.phaseValue = 'idle';
    this.progress = 0;
    this.lostSinceMs = null;
    this.backSinceMs = null;
  }
}
